/**
 * @file     ConsentChoices.tsx
 * @purpose  The optional, unticked email and SMS consent boxes and the identity
 *           line under them, on every form that takes contact details. Either
 *           box may stay empty; the form submits the same way.
 */
'use client';

import { useCallback, useId, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { SEMANTIC_CSS_VARS } from '@/design-system/tokens/semanticColors';
import { brandConfig } from '@/core/config/brand/brandConfig';
import { EMAIL_CONSENT_COPY, EMAIL_CONSENT_VERSION } from '@/core/config/emailConsent';
import { CONTACT_CONSENT_VERSION, FORM_CONSENT_COPY, SMS_CONSENT_VERSION } from '@/core/config/smsConsent';
import { EMPTY_CONSENT, consentChoicesPayload, type ConsentPayload, type ConsentValues } from './consentPayload';

export type { ConsentValues } from './consentPayload';

type Props = {
  /** Show the SMS box. Only forms that take a mobile number pass this. */
  phone?: boolean;
  /** Show the email box. On by default. */
  email?: boolean;
  /** With both channels shown, ask once: one box covers email and text. */
  combined?: boolean;
  values: ConsentValues;
  onChange: (values: ConsentValues) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

function ConsentBox({
  name,
  testId,
  version,
  checked,
  disabled,
  label,
  onChange,
}: {
  name: string;
  testId: string;
  version: string;
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  const labelId = useId();
  return (
    <Box
      component="label"
      data-testid={testId}
      data-consent-version={version}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: disabled ? 'default' : 'pointer' }}
    >
      <Checkbox
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        size="small"
        inputProps={{ 'aria-labelledby': labelId }}
        sx={{ p: 0.5, ml: -0.5, mt: -0.25, color: 'inherit' }}
      />
      <Typography component="span" id={labelId} sx={{ fontSize: 13, lineHeight: 1.5, color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );
}

export function ConsentChoices({ phone = false, email = true, combined = false, values, onChange, disabled = false, sx }: Props) {
  const { t } = useTranslation();
  if (!phone && !email) return null;
  const single = combined && phone && email;
  return (
    <Box
      role="group"
      aria-label={t('forms.consent.choicesLabel', { defaultValue: 'Your contact choices' })}
      data-testid="consent-choices"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        color: `var(${SEMANTIC_CSS_VARS.textSecondary})`,
        ...(sx as object),
      }}
    >
      {single ? (
        <ConsentBox
          name="contactOptIn"
          testId="contact-consent"
          version={CONTACT_CONSENT_VERSION}
          checked={values.email && values.sms}
          disabled={disabled}
          label={t('forms.consent.contactOptIn', { defaultValue: FORM_CONSENT_COPY.contactOptIn })}
          onChange={(checked) => onChange({ email: checked, sms: checked })}
        />
      ) : null}
      {email && !single ? (
        <ConsentBox
          name="emailOptIn"
          testId="email-consent"
          version={EMAIL_CONSENT_VERSION}
          checked={values.email}
          disabled={disabled}
          label={t('forms.consent.emailOptIn', { defaultValue: EMAIL_CONSENT_COPY.emailOptIn })}
          onChange={(checked) => onChange({ ...values, email: checked })}
        />
      ) : null}
      {phone && !single ? (
        <ConsentBox
          name="smsOptIn"
          testId="sms-consent"
          version={SMS_CONSENT_VERSION}
          checked={values.sms}
          disabled={disabled}
          label={t('forms.consent.smsOptIn', { defaultValue: FORM_CONSENT_COPY.smsOptIn })}
          onChange={(checked) => onChange({ ...values, sms: checked })}
        />
      ) : null}
      <Typography
        component="p"
        data-testid="consent-identity"
        sx={{ m: 0, fontSize: 12, lineHeight: 1.5, color: `var(${SEMANTIC_CSS_VARS.textMuted})` }}
      >
        {t('forms.consent.identity', {
          defaultValue: EMAIL_CONSENT_COPY.identity,
          mailingAddress: brandConfig.mailingAddress,
          interpolation: { escapeValue: false },
        })}
      </Typography>
    </Box>
  );
}

/**
 * State for ConsentChoices plus the payload it submits. `payload(phone)` reads
 * the locale and page at submit time, which is what the person saw. Pass
 * `combined` when the form renders `<ConsentChoices combined>`.
 */
export function useConsentChoices(initial: ConsentValues = EMPTY_CONSENT, options: { combined?: boolean } = {}) {
  const { i18n } = useTranslation();
  const [values, setValues] = useState<ConsentValues>(initial);
  const combined = options.combined ?? false;
  const payload = useCallback(
    (phone?: unknown): ConsentPayload => consentChoicesPayload(values, phone, { locale: i18n?.language, combined }),
    [values, i18n?.language, combined],
  );
  const reset = useCallback(() => setValues(EMPTY_CONSENT), []);
  return { values, setValues, payload, reset };
}
